import type { DataBlock, SingleBlock } from 'de-zurich-ui-library'

import { n, t } from '@/i18n'
import type { MonetaryAmount } from 'policies-api'
import { formatDate } from '@/utils/policy/PolicyUtils'
import type {
  ComprehensiveInsurance,
  LiabilityInsurance,
  PassengerInsurance
} from '@/types/motor/MotorInsurance'
import type { VersichertesFahrzeug } from '@/types/policy/PolicyTypes'
import { isEMoped, swapLettersAndNumbers } from '@/utils/motor/MotorUtilsCommon'
import type { MotorContract } from '@/types/motor/MotorContract'

export const INSURED_VEHICLE_ACTION_LINK_ID: string = 'insuredVehicleActionLink'

export function buildInsuredVehicleSection(
  insuredVehicle: VersichertesFahrzeug,
  vehicleChangeEnabled: boolean
): DataBlock {
  let actionLink

  if (vehicleChangeEnabled) {
    actionLink = {
      id: INSURED_VEHICLE_ACTION_LINK_ID,
      label: t('motorDetails.insuredVehicle.actionLink')
    }
  }

  const licensePlateRegex = /^[A-Za-zäÄöÖüÜß -]+\d+[ -]?E?H?$/
  const showLicensePlateIcon = !licensePlateRegex.test(insuredVehicle.licenceNumber)
  let licensePlateDataBox
  if (showLicensePlateIcon) {
    licensePlateDataBox = createTextDataboxLineWithIcon(
      insuredVehicle,
      'motorDetails.insuredVehicle.',
      'licenceNumber',
      'icon-vorteile',
      'openModal_licensePlate'
    )
  } else {
    licensePlateDataBox = createTextDataboxLine(
      insuredVehicle,
      'motorDetails.insuredVehicle.',
      'licenceNumber'
    )
  }

  const eMopedRegex = /^[A-Z]{3}\d{3}$/
  const isEMopedLicenseNumber = eMopedRegex.test(insuredVehicle.licenceNumber)
  if (isEMopedLicenseNumber) {
    const insurerLicensePlate = swapLettersAndNumbers(insuredVehicle.licenceNumber)
    licensePlateDataBox = {
      label: t('motorDetails.insuredVehicle.insurerLicenseNumber'),
      value: insurerLicensePlate,
      hide: !insuredVehicle.licenceNumber
    }
  }

  return {
    title: {
      label: t('motorDetails.insuredVehicle.title'),
      actionLink
    },
    data: [
      licensePlateDataBox,
      createDateDataboxLine(
        insuredVehicle,
        'motorDetails.insuredVehicle.',
        'firstRegistration'
      ),
      createTextDataboxLine(
        insuredVehicle,
        'motorDetails.insuredVehicle.',
        'manufacturer'
      ),
      createTextDataboxLine(insuredVehicle, 'motorDetails.insuredVehicle.', 'modelType'),
      createTextDataboxLine(
        insuredVehicle,
        'motorDetails.insuredVehicle.',
        'manufacturerId'
      ),
      createTextDataboxLine(insuredVehicle, 'motorDetails.insuredVehicle.', 'typeKey'),
      createTextDataboxLine(
        insuredVehicle,
        'motorDetails.insuredVehicle.',
        'vehicleIdentificationNumber'
      ),
      createTextDataboxLine(
        insuredVehicle,
        'motorDetails.insuredVehicle.',
        'parkingSpace'
      ),
      createNumericDataboxLine(insuredVehicle, 'motorDetails.insuredVehicle.', 'power'),
      createCurrencyDataboxLine(
        insuredVehicle,
        'motorDetails.insuredVehicle.',
        'reinstatementValue'
      ),
      createNumericDataboxLine(insuredVehicle, 'motorDetails.insuredVehicle.', 'payload'),
      createTextDataboxLine(
        insuredVehicle,
        'motorDetails.insuredVehicle.',
        'nightlyParkingPlace'
      )
    ]
  }
}

export function buildLiabilityInsuranceSection(
  liabilityInsurance: LiabilityInsurance
): DataBlock {
  const dataList: SingleBlock[] = [
    {
      label: t('motorDetails.liabilityInsurance.dateOfGeneralMotorInsuranceTerms', {
        date: liabilityInsurance.dateOfGeneralMotorInsuranceTerms
      }),
      value: null,
      hide: !liabilityInsurance.dateOfGeneralMotorInsuranceTerms
    }
  ]

  if (liabilityInsurance.noClaimsClass) {
    dataList.push(
      createTextDataboxLine(
        liabilityInsurance,
        'motorDetails.liabilityInsurance.',
        'noClaimsClass'
      )
    )
  }
  if (liabilityInsurance.contributionRateOfNoClaimsClass) {
    dataList.push(
      createNumericDataboxLine(
        liabilityInsurance,
        'motorDetails.liabilityInsurance.',
        'contributionRateOfNoClaimsClass'
      )
    )
  }
  return {
    title: {
      label: t('motorDetails.liabilityInsurance.title')
    },
    data: dataList
  }
}

function determineComprehensiveInsuranceType(
  motorContract: MotorContract,
  insurance: ComprehensiveInsurance
): string {
  if (isEMoped(motorContract.vehicleType.code)) {
    return t('motorDetails.comprehensiveInsurance.comprehensive')
  }
  if (insurance.type) {
    return t(`motorDetails.comprehensiveInsurance.${insurance.type}`)
  }
  if (insurance.deductibleOfFullyComprehensiveInsurance) {
    return t('motorDetails.comprehensiveInsurance.fullyComprehensive')
  }
  return t('motorDetails.comprehensiveInsurance.partiallyComprehensive')
}

export function buildPassengerAccidentInsuranceSection(
  passengerAccidentInsurance: PassengerInsurance
): DataBlock {
  return {
    title: {
      label: t('motorDetails.passengerAccidentInsurance.title')
    },
    data: [
      createCurrencyDataboxLine(
        passengerAccidentInsurance,
        'motorDetails.passengerAccidentInsurance.',
        'insuredAmountOfDeath'
      ),
      createCurrencyDataboxLine(
        passengerAccidentInsurance,
        'motorDetails.passengerAccidentInsurance.',
        'insuredAmountOfInvalidity'
      )
    ]
  }
}

export function buildComprehensiveInsuranceSection(
  motorContract: MotorContract,
  comprehensiveInsurance: ComprehensiveInsurance
): DataBlock {
  const comprehensiveType = determineComprehensiveInsuranceType(
    motorContract,
    comprehensiveInsurance
  )

  const dataList: SingleBlock[] = [
    {
      label: t('motorDetails.comprehensiveInsurance.dateOfGeneralMotorInsuranceTerms', {
        date: comprehensiveInsurance.dateOfGeneralMotorInsuranceTerms
      }),
      value: null,
      hide: !comprehensiveInsurance.dateOfGeneralMotorInsuranceTerms
    }
  ]

  if (comprehensiveInsurance.noClaimsClass) {
    dataList.push(
      createTextDataboxLine(
        comprehensiveInsurance,
        'motorDetails.comprehensiveInsurance.',
        'noClaimsClass'
      )
    )
  }
  if (comprehensiveInsurance.contributionRateOfNoClaimsClass) {
    dataList.push(
      createNumericDataboxLine(
        comprehensiveInsurance,
        'motorDetails.comprehensiveInsurance.',
        'contributionRateOfNoClaimsClass'
      )
    )
  }

  if (comprehensiveInsurance.deductibleOfFullyComprehensiveInsurance) {
    dataList.push(
      createCurrencyDataboxLine(
        comprehensiveInsurance,
        'motorDetails.comprehensiveInsurance.',
        'deductibleOfFullyComprehensiveInsurance'
      )
    )
  }

  if (comprehensiveInsurance.deductibleOfPartiallyComprehensiveInsurance) {
    dataList.push(
      createCurrencyDataboxLine(
        comprehensiveInsurance,
        'motorDetails.comprehensiveInsurance.',
        'deductibleOfPartiallyComprehensiveInsurance'
      )
    )
  }

  if (comprehensiveInsurance.alternateText) {
    dataList.push(
      createTextDataboxLine(
        comprehensiveInsurance,
        'motorDetails.comprehensiveInsurance.',
        'alternateText'
      )
    )
  }

  if (comprehensiveInsurance && isEMoped(motorContract.vehicleType.code)) {
    dataList.push({
      label: t('motorDetails.comprehensiveInsurance.hasComprehensiveInsurance'),
      value: t('motorDetails.comprehensiveInsurance.emopedKaskoInfo'),
      hide: false
    })
  }

  return {
    title: {
      label: t('motorDetails.comprehensiveInsurance.title', {
        comprehensiveType
      })
    },
    data: dataList
  }
}

export function createTextDataboxLine(
  dataObject: any,
  translationKeyPrefix: string,
  dataKey: string
): SingleBlock {
  return {
    label: t(translationKeyPrefix + dataKey),
    value: dataObject[dataKey],
    hide: !dataObject[dataKey]
  }
}

export function createTextDataboxLineWithIcon(
  dataObject: any,
  translationKeyPrefix: string,
  dataKey: string,
  iconClass: string,
  iconHref: string
): SingleBlock {
  return {
    label: t(translationKeyPrefix + dataKey),
    value: dataObject[dataKey],
    hide: !dataObject[dataKey],
    iconSpan: {
      iconClass: iconClass,
      href: iconHref,
      isRouterLink: false
    }
  }
}

export function createDateDataboxLine(
  dateObject: any,
  translationKeyPrefix: string,
  dataKey: string
): SingleBlock {
  return {
    label: t(translationKeyPrefix + dataKey),
    value: formatDate(dateObject[dataKey]),
    hide: !dateObject[dataKey]
  }
}

export function createCurrencyDataboxLine(
  currencyObject: any,
  translationKeyPrefix: string,
  dataKey: string
): SingleBlock {
  return {
    label: t(translationKeyPrefix + dataKey),
    value: formatCurrency(currencyObject[dataKey]),
    hide: !currencyObject[dataKey]
  }
}

export function createNumericDataboxLine(
  numericObject: any,
  translationKeyPrefix: string,
  dataKey: string
): SingleBlock {
  if (numericObject?.[dataKey]) {
    const numericObjectData = numericObject[dataKey]
    return {
      label: t(translationKeyPrefix + dataKey),
      value: numericObjectData.value + ' ' + numericObjectData.unit,
      hide: false
    }
  }
  return {
    label: '',
    value: null,
    hide: true
  }
}

export function formatCurrency(monetaryAmount: MonetaryAmount): string {
  if (monetaryAmount?.amount != null && monetaryAmount?.currency) {
    return n(monetaryAmount.amount, {
      key: 'currency',
      currency: monetaryAmount.currency,
      locale: 'de-DE'
    }).toString()
  }
  return ''
}

export function formatKilometers(kilometers: number): string {
  if (kilometers === undefined || kilometers === null) {
    return ''
  } else {
    return (
      n(kilometers, {
        locale: 'de-DE'
      }) + ' km'
    )
  }
}
